import {
  Activity,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock3,
  DoorOpen,
  LoaderCircle,
  PawPrint,
  Play,
  RefreshCw,
  Stethoscope,
  TimerReset,
  UserCheck,
  UserX,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  api,
  type AppointmentStatus,
  type CrmLocation,
  type CrmOperationalAppointment,
  type CrmOperationsOverview,
} from "./api";
import { useCrm } from "./crm-context";

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  PLANNED: "Planificado",
  CONFIRMED: "Confirmado",
  CHECKED_IN: "En espera",
  IN_PROGRESS: "En atención",
  COMPLETED: "Completado",
  CANCELLED: "Cancelado",
  NO_SHOW: "Ausente",
};

function argentinaDay(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatDay(value: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${value}T12:00:00-03:00`));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function messageFor(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

function metric(value: number | null, suffix: string): string {
  return value === null ? "Sin datos" : `${value}${suffix}`;
}

interface AppointmentCardProps {
  appointment: CrmOperationalAppointment;
  pending: boolean;
  onTransition: (
    appointment: CrmOperationalAppointment,
    status: Exclude<AppointmentStatus, "PLANNED">,
  ) => void;
}

function AppointmentCard({ appointment, pending, onTransition }: AppointmentCardProps): ReactNode {
  const { capabilities } = appointment;
  const active = appointment.status === "CHECKED_IN" || appointment.status === "IN_PROGRESS";
  return (
    <article className={`operations-appointment${active ? " is-active" : ""}`}>
      <div className="operations-appointment-time">
        <time dateTime={appointment.startsAt}>{formatTime(appointment.startsAt)}</time>
        <small>{appointment.durationMinutes} min</small>
      </div>
      <span className="operations-pet-avatar" aria-hidden="true">
        {appointment.petName.charAt(0).toUpperCase()}
      </span>
      <div className="operations-appointment-main">
        <div>
          <h3>{appointment.petName}</h3>
          <span className={`status status-${appointment.status.toLowerCase()}`}>
            {STATUS_LABEL[appointment.status]}
          </span>
        </div>
        <p>{appointment.title}</p>
        <small>
          {appointment.petSpecies === "DOG" ? "Perro" : "Gato"}
          {appointment.petBreed ? ` · ${appointment.petBreed}` : ""}
          {appointment.location ? ` · ${appointment.location}` : ""}
        </small>
        {appointment.status === "CHECKED_IN" && appointment.waitMinutes !== null && (
          <span className={`operations-clock${appointment.waitMinutes > 15 ? " is-delayed" : ""}`}>
            <Clock3 size={14} /> Espera {appointment.waitMinutes} min
          </span>
        )}
        {appointment.status === "IN_PROGRESS" && appointment.startedAt && (
          <span className="operations-clock">
            <Stethoscope size={14} /> Inició {formatTime(appointment.startedAt)}
          </span>
        )}
        {["PLANNED", "CONFIRMED"].includes(appointment.status) && appointment.delayMinutes > 0 && (
          <span className="operations-clock is-delayed">
            <TimerReset size={14} /> Demora {appointment.delayMinutes} min
          </span>
        )}
      </div>
      <div className="operations-appointment-actions">
        {capabilities.canOpenAttention && (
          <Link
            className="button button-primary button-compact"
            to={`/atenciones?patient=${appointment.petId}`}
          >
            <Stethoscope size={16} /> Abrir atención
          </Link>
        )}
        {capabilities.canStart && (
          <button
            className="button button-primary button-compact"
            disabled={pending}
            onClick={() => onTransition(appointment, "IN_PROGRESS")}
          >
            {pending ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}
            Iniciar atención
          </button>
        )}
        {capabilities.canCheckIn && (
          <button
            className="button button-primary button-compact"
            disabled={pending}
            onClick={() => onTransition(appointment, "CHECKED_IN")}
          >
            {pending ? <LoaderCircle className="spin" size={16} /> : <DoorOpen size={16} />}
            Registrar llegada
          </button>
        )}
        {capabilities.canComplete && (
          <button
            className="button button-secondary button-compact button-success"
            disabled={pending}
            onClick={() => onTransition(appointment, "COMPLETED")}
          >
            {pending ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
            Completar jornada
          </button>
        )}
        {capabilities.canConfirm && (
          <button
            className="button-link"
            disabled={pending}
            onClick={() => onTransition(appointment, "CONFIRMED")}
          >
            Confirmar
          </button>
        )}
        <Link className="button-link" to={`/pacientes/${appointment.petId}`}>
          Ver ficha
        </Link>
        {capabilities.canNoShow && (
          <button
            className="button-link button-link-danger"
            disabled={pending}
            onClick={() => onTransition(appointment, "NO_SHOW")}
          >
            Marcar ausente
          </button>
        )}
        {capabilities.canCancel && (
          <button
            className="button-link button-link-danger"
            disabled={pending}
            onClick={() => onTransition(appointment, "CANCELLED")}
          >
            Cancelar
          </button>
        )}
      </div>
    </article>
  );
}

export function OperationsScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [day, setDay] = useState(argentinaDay);
  const [windowDays, setWindowDays] = useState<7 | 30>(7);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<CrmLocation[]>([]);
  const [data, setData] = useState<CrmOperationsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      const [overview, sites] = await Promise.all([
        api.operations(activeOrganization.id, day, windowDays, locationId || undefined),
        api.locations(activeOrganization.id),
      ]);
      setData(overview);
      setLocations(sites);
    } catch (reason) {
      setError(messageFor(reason, "No pudimos cargar el pulso de la jornada."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, day, locationId, windowDays]);

  useEffect(() => void load(), [load]);

  async function transition(
    appointment: CrmOperationalAppointment,
    status: Exclude<AppointmentStatus, "PLANNED">,
  ): Promise<void> {
    if (!activeOrganization) return;
    if (
      ["CANCELLED", "NO_SHOW", "COMPLETED"].includes(status) &&
      !window.confirm(
        status === "NO_SHOW"
          ? `¿Marcar a ${appointment.petName} como ausente?`
          : status === "CANCELLED"
            ? `¿Cancelar el turno de ${appointment.petName}?`
            : `¿Cerrar la jornada de ${appointment.petName} como completada?`,
      )
    )
      return;
    setPendingId(appointment.id);
    setError(null);
    setNotice(null);
    try {
      await api.transitionAppointment(activeOrganization.id, appointment.id, status);
      setNotice(
        status === "CHECKED_IN"
          ? `${appointment.petName} ingresó a la sala de espera.`
          : status === "IN_PROGRESS"
            ? `La atención de ${appointment.petName} está en curso.`
            : `El turno de ${appointment.petName} quedó como ${STATUS_LABEL[status].toLowerCase()}.`,
      );
      await load();
    } catch (reason) {
      setError(messageFor(reason, "No pudimos actualizar el estado del turno."));
    } finally {
      setPendingId(null);
    }
  }

  const groups = useMemo(() => {
    const appointments = data?.appointments ?? [];
    return {
      active: appointments.filter((item) => ["CHECKED_IN", "IN_PROGRESS"].includes(item.status)),
      upcoming: appointments.filter((item) => ["PLANNED", "CONFIRMED"].includes(item.status)),
      closed: appointments.filter((item) =>
        ["COMPLETED", "CANCELLED", "NO_SHOW"].includes(item.status),
      ),
    };
  }, [data?.appointments]);

  if (!activeOrganization) return null;

  const maxTrend = Math.max(1, ...(data?.trend.map((item) => item.scheduledCount) ?? [1]));
  return (
    <div className="screen operations-screen">
      <header className="page-header operations-header">
        <div>
          <span className="eyebrow">Torre de control clínica</span>
          <h1>Jornada</h1>
          <p>Recepción, espera, atención y cierre en una sola lectura operativa.</p>
        </div>
        <div className="operations-header-controls">
          <label>
            <span>Día operativo</span>
            <input
              type="date"
              value={day}
              required
              onChange={(event) => {
                if (event.target.value) setDay(event.target.value);
              }}
            />
          </label>
          <label>
            <span>Sede</span>
            <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
              <option value="">Todas las sedes</option>
              {locations.map((location) => (
                <option value={location.id} key={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <button
            className="icon-button"
            type="button"
            onClick={() => void load()}
            aria-label="Actualizar jornada"
            disabled={loading}
          >
            <RefreshCw className={loading ? "spin" : ""} size={19} />
          </button>
        </div>
      </header>

      {error && (
        <div className="error-panel" role="alert">
          <Activity size={20} />
          <div>
            <strong>No pudimos actualizar la jornada</strong>
            <p>{error}</p>
          </div>
          <button className="button button-secondary button-compact" onClick={() => void load()}>
            Reintentar
          </button>
        </div>
      )}
      {notice && (
        <div className="success-panel" role="status">
          <CheckCircle2 size={18} />
          <span>{notice}</span>
          <button className="icon-button" onClick={() => setNotice(null)} aria-label="Cerrar aviso">
            <X size={16} />
          </button>
        </div>
      )}

      {loading && !data ? (
        <div className="operations-skeleton" role="status" aria-label="Cargando jornada">
          <span />
          <span />
          <span />
          <span />
        </div>
      ) : data ? (
        <>
          <section className="operations-pulse" aria-labelledby="operations-pulse-title">
            <div className="operations-pulse-heading">
              <span>
                <Activity size={19} aria-hidden="true" />
                <span>
                  <small>{formatDay(data.day)}</small>
                  <strong id="operations-pulse-title">
                    Pulso de jornada{locationId ? " por sede" : ""}
                  </strong>
                </span>
              </span>
              <small>Actualizado {formatTime(data.generatedAt)}</small>
            </div>
            <div className="operations-pulse-track">
              <div>
                <CalendarClock size={19} />
                <strong>{data.pulse.upcomingCount}</strong>
                <span>Próximos</span>
              </div>
              <i aria-hidden="true" />
              <div className={data.pulse.waitingCount ? "is-live" : ""}>
                <UserCheck size={19} />
                <strong>{data.pulse.waitingCount}</strong>
                <span>En espera</span>
              </div>
              <i aria-hidden="true" />
              <div className={data.pulse.inProgressCount ? "is-live" : ""}>
                <Stethoscope size={19} />
                <strong>{data.pulse.inProgressCount}</strong>
                <span>En atención</span>
              </div>
              <i aria-hidden="true" />
              <div>
                <CheckCircle2 size={19} />
                <strong>{data.pulse.completedCount}</strong>
                <span>Completados</span>
              </div>
            </div>
          </section>

          <section className="operations-performance" aria-labelledby="performance-title">
            <div className="operations-section-title">
              <div>
                <span className="eyebrow">Rendimiento real</span>
                <h2 id="performance-title">Últimos {data.performance.windowDays} días</h2>
              </div>
              <div className="segmented-control" aria-label="Ventana de reporte">
                {([7, 30] as const).map((value) => (
                  <button
                    className={windowDays === value ? "active" : ""}
                    key={value}
                    onClick={() => setWindowDays(value)}
                    aria-pressed={windowDays === value}
                  >
                    {value} días
                  </button>
                ))}
              </div>
            </div>
            <div className="operations-metric-grid">
              <article>
                <Clock3 size={18} />
                <span>
                  <small>Espera mediana</small>
                  <strong>{metric(data.performance.medianWaitMinutes, " min")}</strong>
                </span>
              </article>
              <article>
                <Stethoscope size={18} />
                <span>
                  <small>Duración promedio</small>
                  <strong>{metric(data.performance.averageAttentionMinutes, " min")}</strong>
                </span>
              </article>
              <article>
                <TimerReset size={18} />
                <span>
                  <small>Inicio a tiempo</small>
                  <strong>{metric(data.performance.onTimeStartRate, "%")}</strong>
                </span>
              </article>
              <article>
                <UserX size={18} />
                <span>
                  <small>Ausentismo</small>
                  <strong>{metric(data.performance.noShowRate, "%")}</strong>
                </span>
              </article>
            </div>
            <div
              className={`operations-trend${data.trend.length > 7 ? " is-condensed" : ""}`}
              role="img"
              aria-label={`Volumen de turnos de los últimos ${data.trend.length} días`}
            >
              {data.trend.map((item) => (
                <span
                  key={item.day}
                  title={`${item.day}: ${item.scheduledCount} turnos`}
                  aria-label={`${item.day}, ${item.scheduledCount} turnos, ${item.completedCount} completados`}
                  style={
                    {
                      "--trend-height": `${Math.max(8, (item.scheduledCount / maxTrend) * 100)}%`,
                      "--trend-completed": `${Math.max(
                        0,
                        (item.completedCount / maxTrend) * 100,
                      )}%`,
                    } as CSSProperties
                  }
                >
                  <i />
                  <b />
                  {data.trend.length <= 7 && <small>{item.day.slice(8)}</small>}
                </span>
              ))}
            </div>
          </section>

          <div className="operations-board">
            <section
              className="operations-lane operations-lane-active"
              aria-labelledby="active-lane"
            >
              <div className="operations-section-title">
                <div>
                  <span className="eyebrow">Prioridad ahora</span>
                  <h2 id="active-lane">Sala y atención</h2>
                </div>
                <span className="count">{groups.active.length}</span>
              </div>
              {groups.active.length ? (
                <div className="operations-list">
                  {groups.active.map((appointment) => (
                    <AppointmentCard
                      appointment={appointment}
                      key={appointment.id}
                      pending={pendingId === appointment.id}
                      onTransition={(item, status) => void transition(item, status)}
                    />
                  ))}
                </div>
              ) : (
                <div className="operations-empty">
                  <UserCheck size={25} />
                  <span>
                    <strong>Sala despejada</strong>
                    <small>Las llegadas registradas aparecerán acá.</small>
                  </span>
                </div>
              )}
            </section>

            <section className="operations-lane" aria-labelledby="upcoming-lane">
              <div className="operations-section-title">
                <div>
                  <span className="eyebrow">Siguiente movimiento</span>
                  <h2 id="upcoming-lane">Próximos turnos</h2>
                </div>
                <span className="count">{groups.upcoming.length}</span>
              </div>
              {groups.upcoming.length ? (
                <div className="operations-list">
                  {groups.upcoming.map((appointment) => (
                    <AppointmentCard
                      appointment={appointment}
                      key={appointment.id}
                      pending={pendingId === appointment.id}
                      onTransition={(item, status) => void transition(item, status)}
                    />
                  ))}
                </div>
              ) : (
                <div className="operations-empty">
                  <PawPrint size={25} />
                  <span>
                    <strong>Sin próximos turnos</strong>
                    <small>La agenda de este día no tiene pacientes pendientes.</small>
                  </span>
                </div>
              )}
            </section>
          </div>

          {groups.closed.length > 0 && (
            <details className="panel operations-closed">
              <summary>
                <span>
                  <CheckCircle2 size={18} />
                  Cierres del día
                </span>
                <span className="count">{groups.closed.length}</span>
              </summary>
              <div className="operations-list">
                {groups.closed.map((appointment) => (
                  <AppointmentCard
                    appointment={appointment}
                    key={appointment.id}
                    pending={false}
                    onTransition={(item, status) => void transition(item, status)}
                  />
                ))}
              </div>
            </details>
          )}
        </>
      ) : null}
    </div>
  );
}
